import { useReviewContext } from "@hooks/useReviewSnippet";
import { useRoutes } from "@hooks/useRoutes";
import { useShopify } from "@hooks/useShopify";
import { useSite } from "@hooks/useSite";
import { useRouteLoaderData } from "~/hooks/useRouteLoaderData";
import { useCurrency } from "~/hooks/useCurrency";

import {
  type Badge,
  type ComponentProps,
  type ResolvedLink,
} from "@ts/components";
import {
  ElasticCollection,
  ElasticImage,
  ElasticProduct,
} from "@usereactify/search";
import type { MoneyV2, CurrencyCode } from "@ts/storefront.types";
import React, { FC, memo, useEffect, useMemo } from "react";

export type SearchResultsProductCardInputProps = ComponentProps & {
  pagePosition: number;
  collection: ElasticCollection;
  product: ElasticProduct;
};

export type SearchResultsProductCardOutputProps =
  SearchResultsProductCardInputProps & {
    badge: Badge;
    price: string;
    compareAtPrice: string;
    hiddenTitle: string;
    hoverImage: ElasticImage;
    image: ElasticImage;
    isGiftCard: boolean;
    link: ResolvedLink;
    productGroup: string;
    subTitle: string;
    tags: string[];
    productName: string;
  };

// Function to calculate pricing
const calculatePricing = (
  product: ElasticProduct,
  currencyCode: CurrencyCode,
  salePrice: number,
  isProductOnSale: boolean,
): { price: MoneyV2; compareAtPrice: MoneyV2 | null } | undefined => {
  // Get the highest price and compare at price for the product
  const prices = product.variants?.reduce(
    (acc, variant) => {
      const currentPrices = variant.presentment_prices?.find(
        (p) => p.price?.currency_code === currencyCode,
      );

      const price = Number(currentPrices?.price?.amount) || 0;
      const comparePrice = Number(currentPrices?.compare_at_price?.amount) || 0;

      return {
        price: {
          amount: Math.max(price, acc.price.amount),
          currencyCode,
        },
        compareAtPrice: {
          amount: Math.max(comparePrice, acc.compareAtPrice.amount),
          currencyCode,
        },
      };
    },
    {
      price: { amount: 0, currencyCode },
      compareAtPrice: { amount: 0, currencyCode },
    },
  );

  // Return appropriate pricing

  // If sale is active and product has a sale price discount
  if (isProductOnSale && prices && salePrice < prices.price?.amount)
    return {
      price: { amount: salePrice.toString(), currencyCode },
      compareAtPrice: { amount: prices.price.amount.toString(), currencyCode },
    };

  // Product has regular compare at price discount
  if (prices && prices.compareAtPrice?.amount > prices.price?.amount)
    return {
      price: { amount: prices.price.amount.toString(), currencyCode },
      compareAtPrice: {
        amount: prices.compareAtPrice.amount.toString(),
        currencyCode,
      },
    };

  // Product has no discount
  if (prices && prices.price?.amount > 0)
    return {
      price: { amount: prices?.price?.amount.toString(), currencyCode },
      compareAtPrice: null,
    };

  return undefined;
};

export const withSearchResultsProductCard = (
  Component: FC<SearchResultsProductCardOutputProps>,
) => {
  return memo(
    ({
      name = "SearchResultsProductCard",
      product,
      collection,
      pagePosition,
    }: SearchResultsProductCardInputProps) => {
      const { enableSale } = useRouteLoaderData("routes/$site");
      const { addProductToQueue } = useReviewContext();
      const { formatProductGroup, getBadgeFromTags } = useShopify();
      const { linkResolver } = useRoutes();
      const site = useSite();
      const { formatCurrency } = useCurrency();
      const { tags } = product || [];
      const productName = product.metafields
        ? product.metafields.find(
            (metafield) => metafield?.key === "netsuite_style_name",
          )?.value
        : "";
      const productStatus = product.metafields
        ? product.metafields.find(
            (metafield) => metafield?.key === "netsuite_status",
          )?.value
        : "";
      const launchDateMetafield = product.metafields
        ? product.metafields.find(
            (metafield) => metafield?.key === "launch_date",
          )?.value
        : "";
      const salePriceMetafield = product.metafields
        ? product.metafields.find(
            (metafield) =>
              metafield?.key ===
              `sale_price_${site.currencyCode?.toLowerCase()}`,
          )?.value
        : "";
      const salePrice = salePriceMetafield ? Number(salePriceMetafield) : 0;
      const isProductOnSale = salePrice > 0 && enableSale;
      const productPrice = calculatePricing(
        product,
        site.currencyCode,
        salePrice,
        isProductOnSale,
      );

      // Format the price and compare at price for the display
      const pricePrefix = site.currencyCode === "GBP" ? "" : site.currencyCode;
      const formattedPrice = formatCurrency(
        productPrice?.price?.amount,
        productPrice?.price?.currencyCode,
        0,
      )
        ? `${pricePrefix}${formatCurrency(productPrice?.price?.amount, productPrice?.price?.currencyCode, 0)}`
        : "";
      const formattedCompareAtPrice = formatCurrency(
        productPrice?.compareAtPrice?.amount,
        productPrice?.compareAtPrice?.currencyCode,
        0,
      )
        ? `${pricePrefix}${formatCurrency(productPrice?.compareAtPrice?.amount, productPrice?.compareAtPrice?.currencyCode, 0)}`
        : "";

      const hasColourOption = product.options.some(
        (item) => item.includes("Color") || item.includes("Colour"),
      );
      const subTitle =
        hasColourOption && product.variant_options
          ? product.variant_options[0]
          : "";
      const hiddenTitle = product?.title
        ?.replace(productName, ``)
        ?.replace(subTitle, ``)
        ?.trim();
      const link = {
        ...linkResolver(product),
        url: product.url,
      };
      const image = {
        src: product?.images?.[0]?.src,
      };
      const hoverImage = {
        src: product?.images?.[1]?.src,
      };

      const availableForSale =
        product.variants?.some((variant) => variant.available === true) ??
        false;
      const isGiftCard = tags?.includes("type:giftcard");
      const badge = getBadgeFromTags(
        tags,
        productStatus,
        availableForSale,
        launchDateMetafield,
        productPrice,
      );
      const productGroup = formatProductGroup(tags);

      useEffect(() => {
        addProductToQueue(product.handle);
      }, [product]);

      Component.displayName = name;

      return useMemo(
        () => (
          <Component
            badge={badge}
            price={formattedPrice}
            compareAtPrice={formattedCompareAtPrice}
            collection={collection}
            hiddenTitle={hiddenTitle}
            hoverImage={hoverImage}
            image={image}
            isGiftCard={isGiftCard}
            link={link}
            product={product}
            productGroup={productGroup}
            subTitle={subTitle}
            title={productName}
            tags={tags}
            pagePosition={pagePosition}
          />
        ),
        [product],
      );
    },
  );
};
